import { useEffect } from 'react';
import type { RefObject } from 'react';
import { TextLayer } from 'pdfjs-dist/build/pdf.mjs';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist/types/src/display/api';

interface Options {
  pdf: PDFDocumentProxy | null;
  pageNumber: number;
  cssScale: number;
  shouldRender: boolean;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  textLayerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Renders one PDF page's canvas + text layer when `shouldRender` is true,
 * tears them down when it flips false, and cancels mid-flight render tasks
 * if the page changes scale or unmounts. The text layer is required for
 * native text selection (used by the highlight tool).
 *
 * Hi-DPI: the canvas's intrinsic pixel size is scaled by `devicePixelRatio`
 * for sharp output, while its CSS size remains `cssWidth × cssHeight`.
 */
export function usePdfPageRender({
  pdf,
  pageNumber,
  cssScale,
  shouldRender,
  canvasRef,
  textLayerRef,
}: Options): void {
  useEffect(() => {
    if (!shouldRender || !pdf) return;

    const ac = new AbortController();
    const aborted = () => ac.signal.aborted;
    let renderTask: RenderTask | null = null;
    let textLayer: TextLayer | null = null;

    const canvas = canvasRef.current;
    const textLayerEl = textLayerRef.current;
    if (!canvas) return;

    void (async () => {
      const page = await pdf.getPage(pageNumber);
      if (aborted()) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: cssScale });
      const cssWidth = viewport.width;
      const cssHeight = viewport.height;

      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(cssHeight * dpr);
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;

      renderTask = page.render({
        canvas,
        viewport,
        transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
      });

      try {
        await renderTask.promise;
      } catch {
        return;
      }
      if (aborted()) return;

      if (textLayerEl) {
        // pdfjs v5's TextLayer CSS sizes spans via `calc(var(--total-scale-factor) * ...)`.
        // Without this CSS variable the spans collapse to font-size: 0 and selection is dead.
        textLayerEl.style.setProperty('--total-scale-factor', String(cssScale));
        textLayerEl.style.width = `${cssWidth}px`;
        textLayerEl.style.height = `${cssHeight}px`;
        textLayerEl.replaceChildren();

        const textContent = await page.getTextContent();
        if (aborted()) return;

        textLayer = new TextLayer({
          textContentSource: textContent,
          container: textLayerEl,
          viewport,
        });
        await textLayer.render();
      }
    })();

    const canvasAtMount = canvasRef.current;
    const textLayerAtMount = textLayerRef.current;
    return () => {
      ac.abort();
      if (renderTask) {
        try {
          renderTask.cancel();
        } catch {
          // already settled
        }
      }
      if (textLayer) {
        try {
          textLayer.cancel();
        } catch {
          // already settled
        }
      }
      if (canvasAtMount) {
        const ctx = canvasAtMount.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasAtMount.width, canvasAtMount.height);
      }
      if (textLayerAtMount) textLayerAtMount.replaceChildren();
    };
  }, [pdf, pageNumber, cssScale, shouldRender, canvasRef, textLayerRef]);
}
